// src/monitoring/pages/Portfolio.jsx - every park of the project at a glance.

import React, { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useMonitoring } from "../MonitoringContext";
import { useMonitoringText } from "../i18n";
import { seedDemo } from "../services/monitoringApi";
import ParkMap from "../components/ParkMap";
import useMonitoringChartTheme from "../components/chartTheme";
import { SectionTitle, StatTile, StatusChip, fmtDate, fmtKw, fmtKwh, fmtKwp, fmtNum, fmtPct, fmtTime, useLocale } from "../components/primitives";

export default function Portfolio() {
  const m = useMonitoring();
  const t = useMonitoringText();
  const locale = useLocale();
  const chartTheme = useMonitoringChartTheme();
  const navigate = useNavigate();
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState(null);
  const pf = m.portfolio;
  const totals = pf?.totals || {};
  const parks = pf?.parks || [];

  const daily = useMemo(() => (pf?.daily || []).map((d) => ({
    day: fmtDate(d.day, locale), actual: Math.round((d.e_kwh || 0) / 100) / 10, expected: Math.round((d.e_expected_kwh || 0) / 100) / 10,
  })), [pf, locale]);

  const loadDemo = async () => {
    setSeeding(true); setSeedError(null);
    try {
      await seedDemo(m.projectId, 14, { accessToken: m.accessToken });
      m.refresh();
    } catch (err) {
      setSeedError(err.message);
    } finally {
      setSeeding(false);
    }
  };

  if (!parks.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6">{t("portfolio.empty")}</Typography>
        <Typography sx={{ my: 1.5, color: "text.secondary" }}>{t("portfolio.emptyHint")}</Typography>
        {seedError && <Alert severity="error" sx={{ mb: 1.5 }}>{seedError}</Alert>}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="contained" component={RouterLink} to="/monitoring/sources">{t("portfolio.goSources")}</Button>
          {m.canSeedDemo && <Button variant="outlined" onClick={loadDemo} disabled={seeding}>{seeding ? t("sources.demoRunning") : t("portfolio.loadDemo")}</Button>}
        </Stack>
      </Paper>
    );
  }

  const alarmsTotal = (totals.open_alarms?.critical || 0) + (totals.open_alarms?.warning || 0) + (totals.open_alarms?.info || 0);
  const prAccent = totals.pr_today == null ? undefined : totals.pr_today >= 0.85 ? "success.main" : totals.pr_today >= 0.7 ? "warning.main" : "error.main";

  return (
    <Stack spacing={2.5}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatTile label={t("portfolio.livePower")} value={fmtKw(totals.p_ac_kw, locale)}
            sub={totals.p_ac_kw_expected != null ? `${fmtKw(totals.p_ac_kw_expected, locale)} ${t("portfolio.expectedNow")}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatTile label={t("portfolio.energyToday")} value={fmtKwh(totals.e_today_kwh, locale)}
            sub={totals.e_expected_today_kwh ? `${fmtKwh(totals.e_expected_today_kwh, locale)} ${t("portfolio.expected").toLowerCase()}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatTile label={t("portfolio.prToday")} value={fmtPct(totals.pr_today, 0, locale)} accent={prAccent}
            sub={`${t("portfolio.installed")}: ${fmtKwp(totals.dc_kwp, locale)}`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatTile label={t("portfolio.openAlarms")} value={fmtNum(alarmsTotal, 0, locale)} accent={totals.open_alarms?.critical ? "error.main" : alarmsTotal ? "warning.main" : "success.main"}>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
              {totals.open_alarms?.critical > 0 && <Chip size="small" color="error" label={`${totals.open_alarms.critical} ${t("severity.critical").toLowerCase()}`} />}
              {totals.open_alarms?.warning > 0 && <Chip size="small" color="warning" label={`${totals.open_alarms.warning} ${t("severity.warning").toLowerCase()}`} />}
              {totals.open_alarms?.info > 0 && <Chip size="small" color="info" label={`${totals.open_alarms.info} ${t("severity.info").toLowerCase()}`} />}
            </Stack>
          </StatTile>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatTile label={t("portfolio.inverters")} value={`${fmtNum(totals.inverters?.reporting, 0, locale)} / ${fmtNum(totals.inverters?.total, 0, locale)}`}
            sub={`${fmtNum(totals.inverters?.producing, 0, locale)} ${t("portfolio.producing")}`} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <SectionTitle title={`${t("portfolio.parks")} (${parks.length})`} />
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["park", "status", "power", "expected", "today", "pr", "inverters", "alarms", "lastReading"].map((k) => (
                      <TableCell key={k} align={["power", "expected", "today", "pr", "inverters", "alarms"].includes(k) ? "right" : "left"}>{t(`portfolio.col.${k}`)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parks.map((p) => {
                    const alarms = (p.alarms?.critical || 0) + (p.alarms?.warning || 0) + (p.alarms?.info || 0);
                    const pendingVerification = (p.verification_pending?.critical || 0) + (p.verification_pending?.warning || 0) + (p.verification_pending?.info || 0);
                    return (
                      <TableRow key={p.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/monitoring/parks/${p.id}`)}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{fmtKwp(p.capacity?.dc_kwp, locale)} · {p.vendor_label}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25} alignItems="flex-start">
                            <StatusChip status={p.status} />
                            {pendingVerification > 0 && <Typography variant="caption" color="warning.main">{t("portfolio.awaitingVerification")}</Typography>}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{fmtKw(p.live?.p_ac_kw, locale)}</TableCell>
                        <TableCell align="right" sx={{ color: "text.secondary" }}>{fmtKw(p.live?.p_ac_kw_expected, locale)}</TableCell>
                        <TableCell align="right">{fmtKwh(p.live?.e_today_kwh, locale)}</TableCell>
                        <TableCell align="right">{fmtPct(p.live?.pr_today, 0, locale)}</TableCell>
                        <TableCell align="right">{p.inverters?.reporting}/{p.inverters?.total}</TableCell>
                        <TableCell align="right">
                          {alarms ? <Chip size="small" color={p.alarms?.critical ? "error" : p.alarms?.warning ? "warning" : "info"} label={alarms} /> : "—"}
                        </TableCell>
                        <TableCell>{fmtTime(p.live?.last_reading_at, locale, p.plant?.timezone)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <SectionTitle title={t("portfolio.map")} />
            <ParkMap parks={parks} height={300} />
          </Paper>
        </Grid>
      </Grid>

      {daily.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <SectionTitle title={t("portfolio.energy30")} />
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} interval="preserveStartEnd" />
                <YAxis tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} unit=" MWh" width={70} />
                <Tooltip {...chartTheme.tooltip} formatter={(v) => `${fmtNum(v, 1, locale)} MWh`} />
                <Legend {...chartTheme.legend} />
                <Bar dataKey="expected" name={t("portfolio.expected")} fill={chartTheme.expectedBar} radius={[3, 3, 0, 0]} />
                <Bar dataKey="actual" name={t("portfolio.actual")} fill="#1976d2" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
